import React, { useEffect, useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Z_INDEX } from '../../utils/zIndex';
import { lockBodyScroll } from '../../utils/bodyScrollLock';
import CloseButton from './CloseButton';

/**
 * Stack of currently-open modals, front-most last. Every Modal portals to
 * document.body and attaches its own document-level keydown listener, so
 * without a shared stack two open modals mean: one Escape closes BOTH, the
 * lower modal's focus trap fights the upper one's, the first to close
 * restores body scroll while the other is still open, and equal z-indexes
 * leave paint order up to which portal mounted last. Identity tokens only —
 * never render off this array (mutating it doesn't re-render).
 */
const modalStack = [];

/**
 * Every surface that claims the keyboard while it is open, front-most last:
 * modals AND the overlays that paint above them (the photo viewers at
 * Z_INDEX.PHOTO_VIEWER). Only the last entry handles Escape and runs a focus
 * trap; everyone below stands down.
 *
 * It has to be ONE stack rather than "modals, plus a flag for overlays",
 * because ownership genuinely alternates. The recap composer is a modal; it
 * opens a lightbox (overlay) over itself; the lightbox's delete button opens a
 * ConfirmDialog (modal) over THAT — and the confirm, which paints on top
 * (Z_INDEX comments explain why a nested modal clears an inline lightbox), has
 * to own Escape and the trap. A boolean "an overlay is open" can't express
 * that: it would leave the confirm standing down to the lightbox underneath
 * it, so Escape closed the photo behind the dialog and Tab never reached the
 * dialog's own buttons.
 *
 * None of this can be solved by stopping propagation: the listeners all sit on
 * `document`, so only stopImmediatePropagation could suppress another, and the
 * lower surface's listener is attached FIRST (it mounted first) — it has
 * already run by the time the upper one's fires.
 *
 * Entries are { token, getElement } — identity tokens only, never render off
 * this array (mutating it doesn't re-render).
 */
const keyboardStack = [];

/**
 * Push `token` as the front-most keyboard owner. `getElement` returns the
 * surface's root node, and is a getter because a ref isn't populated until
 * React attaches it. Returns the pop function, so an effect can
 * `return pushKeyboardOwner(token, getElement)`.
 *
 * Registration order is mount order, which is almost always the answer — a
 * lightbox opens over an already-open composer, a confirm dialog opens over an
 * already-open lightbox. The exception is a surface and something rendered
 * INSIDE it mounting in the same commit: React runs the child's effects first,
 * so the child registers first and the parent would land on top of the thing
 * it contains. So containment wins where it applies, and it is the sharper
 * rule anyway — nothing can outrank a surface it is rendered inside. Modals
 * all portal to document.body, so two of them never contain each other and
 * this never reorders them.
 */
function pushKeyboardOwner(token, getElement = () => null) {
  const entry = { token, getElement };
  const mine = getElement();
  const contained = mine
    ? keyboardStack.findIndex(({ getElement: theirs }) => {
      const el = theirs();
      return el && mine.contains(el);
    })
    : -1;
  if (contained === -1) keyboardStack.push(entry);
  else keyboardStack.splice(contained, 0, entry);
  return () => {
    const index = keyboardStack.indexOf(entry);
    if (index !== -1) keyboardStack.splice(index, 1);
  };
}

/**
 * Does `token` own the keyboard right now? Every Escape handler and focus trap
 * in the app gates on this, so exactly one surface responds to a keypress.
 */
export function ownsKeyboard(token) {
  return keyboardStack[keyboardStack.length - 1]?.token === token;
}

/**
 * Is nothing open that owns the keyboard? For page-level shortcuts (the album
 * page's arrow keys), which must stand down while any dialog or viewer is up.
 */
export function keyboardIsFree() {
  return keyboardStack.length === 0;
}

/**
 * Register a surface that is not a Modal but owns the keyboard just as
 * completely while it is up — the full-screen photo viewers, which paint
 * outside the modal layer. Returns the unregister function, so an effect can
 * `return registerKeyboardOwner(token, getElement)`.
 *
 * It says "keyboard owner" and not "above modals" on purpose: under an ordered
 * stack an overlay doesn't outrank modals as a class, it just takes the top
 * while it is the most recently opened thing. That is what makes the handover
 * work in both directions — a viewer opened from a modal owns the keyboard,
 * and a confirm dialog opened from that viewer takes it straight back. It is
 * also why AlbumDetail's gallery can use this while deliberately painting
 * UNDER the modal layer, so a tag sheet opens on top of it.
 *
 * Pass a STABLE token (a ref's current value, not a fresh `{}` per render): it
 * is what `ownsKeyboard(token)` is asked about, which every one of these
 * surfaces needs the moment it traps focus. Pass `getElement` too, so a
 * surface and something rendered inside it can't invert when they mount in the
 * same commit.
 */
export function registerKeyboardOwner(token, getElement) {
  return pushKeyboardOwner(token, getElement);
}

/**
 * Unified Modal component for consistent modal behavior across the app.
 *
 * @param {boolean} isOpen - Whether the modal is visible
 * @param {function} onClose - Called when modal should close (backdrop click, escape key)
 * @param {React.ReactNode} children - Modal content
 * @param {string} size - Modal width: 'sm' | 'md' | 'lg' | 'xl' | 'full' (default: 'md')
 * @param {string} position - Modal position: 'center' | 'top' | 'bottom' (default: 'center')
 * @param {boolean} closeOnBackdrop - Whether clicking backdrop closes modal (default: true)
 * @param {boolean} closeOnEscape - Whether escape key closes modal (default: true)
 * @param {boolean} showCloseButton - Whether to show X close button (default: false)
 * @param {string} backdrop - Backdrop style: 'default' | 'blur' | 'dark' | 'none' (default: 'default')
 * @param {string} className - Additional classes for the modal container
 * @param {string} contentClassName - Additional classes for the modal content
 * @param {boolean} mobileFullScreen - Whether modal should be full screen on mobile (default: false)
 * @param {string} ariaLabel - Accessibility label for the modal
 */
function Modal({
  isOpen,
  onClose,
  children,
  size = 'md',
  position = 'center',
  closeOnBackdrop = true,
  closeOnEscape = true,
  showCloseButton = false,
  backdrop = 'default',
  className = '',
  contentClassName = '',
  mobileFullScreen = false,
  ariaLabel
}) {
  const { t } = useTranslation();
  const modalRef = useRef(null);
  const previousActiveElement = useRef(null);
  const resolvedAriaLabel = ariaLabel ?? t('modal.dialogAriaLabelDefault');
  // Stable identity for this instance's slot in modalStack.
  const stackToken = useRef({});
  // Depth at push time, so a modal opened on top of another renders above it
  // instead of relying on portal mount order at equal z-index.
  const [stackDepth, setStackDepth] = useState(0);

  // Only the front-most keyboard owner responds — which may be an overlay
  // above this modal, or a modal opened above one of those.
  const isTopmost = useCallback(() => ownsKeyboard(stackToken.current), []);

  // Handle escape key
  const handleEscape = useCallback((e) => {
    if (e.key === 'Escape' && closeOnEscape && onClose && isTopmost()) {
      onClose();
    }
  }, [closeOnEscape, onClose, isTopmost]);

  // Handle backdrop click
  const handleBackdropClick = useCallback((e) => {
    if (e.target === e.currentTarget && closeOnBackdrop && onClose) {
      onClose();
    }
  }, [closeOnBackdrop, onClose]);

  // Focus trap - keep focus within modal
  const handleTabKey = useCallback((e) => {
    if (e.key !== 'Tab' || !modalRef.current || !isTopmost()) return;

    const selector = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
    const inModal = Array.from(modalRef.current.querySelectorAll(selector));
    // Nothing to hold focus on. Let the browser have the key rather than
    // preventDefault into a dead end — a modal with no controls can't trap.
    if (inModal.length === 0) return;
    // A toast's action (Undo) is painted outside every modal but answers the
    // change just made in one, so Tab reaches it after the modal's last
    // control. Toasts opt in with data-modal-companion.
    const companions = Array.from(document.querySelectorAll('[data-modal-companion]'))
      .filter(el => !modalRef.current.contains(el))
      .flatMap(el => Array.from(el.querySelectorAll(selector)));
    const focusableElements = [...inModal, ...companions];

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    const companionAt = companions.indexOf(document.activeElement);
    if (companionAt >= 0) {
      e.preventDefault();
      const at = inModal.length + companionAt;
      const len = focusableElements.length;
      focusableElements[(at + (e.shiftKey ? -1 : 1) + len) % len]?.focus();
      return;
    }

    // Focus that is not inside this modal AT ALL is at neither boundary, so
    // the two wrap tests below can't fire and Tab steps into the page behind.
    // It is not an exotic state: clicking the dim area around a modal doesn't
    // close it (the backdrop is a sibling div, so `e.target !== e.currentTarget`)
    // but it does blur the focused control onto <body>; and a modal opened by
    // keypress doesn't own focus until the 50ms timer below fires, so a fast
    // Tab arrives while focus is still on whatever opened it. Both used to be
    // harmless because a modal under an open photo viewer never ran its trap
    // at all — now that ownership alternates, this modal really is the trap.
    if (!modalRef.current.contains(document.activeElement)) {
      e.preventDefault();
      (e.shiftKey ? lastElement : firstElement)?.focus();
      return;
    }

    if (e.shiftKey && document.activeElement === firstElement) {
      e.preventDefault();
      lastElement?.focus();
    } else if (!e.shiftKey && companions.length && document.activeElement === inModal[inModal.length - 1]) {
      // The browser's next stop after the modal is wherever the page puts it,
      // not the toast: step there by hand.
      e.preventDefault();
      companions[0].focus();
    } else if (!e.shiftKey && document.activeElement === lastElement) {
      e.preventDefault();
      firstElement?.focus();
    }
  }, [isTopmost]);

  // Register in the stack and focus the first focusable element on open
  // (runs only when isOpen transitions to true)
  useEffect(() => {
    if (isOpen) {
      const token = stackToken.current;
      modalStack.push(token);
      // modalStack stays modal-only: it drives stackDepth, and therefore which
      // z-index tokens this modal paints with. Keyboard ownership is the
      // separate, shared question.
      const popKeyboardOwner = pushKeyboardOwner(token, () => modalRef.current);
      const releaseScroll = lockBodyScroll();
      setStackDepth(modalStack.length - 1);
      previousActiveElement.current = document.activeElement;
      // The open dialog's node, for the focus check on close (the ref may be
      // cleared by then).
      const modalNode = modalRef.current;

      const timer = setTimeout(() => {
        if (modalRef.current) {
          const focusableElements = modalRef.current.querySelectorAll(
            'input, select, textarea, button, [href], [tabindex]:not([tabindex="-1"])'
          );
          if (focusableElements.length > 0) {
            focusableElements[0].focus();
          }
        }
      }, 50);

      return () => {
        clearTimeout(timer);
        popKeyboardOwner();
        const index = modalStack.indexOf(token);
        if (index !== -1) modalStack.splice(index, 1);
        // Drops this modal's claim on the background scroll; the count decides
        // whether that actually unlocks the page. It is what stops closing the
        // top of a stack unlocking the background behind the one still open —
        // including when the one still open is not a Modal at all, which is
        // AlbumDetail's gallery under its place-insights dialog.
        releaseScroll();
        // Hand focus back only when nothing else has taken it: focus lost to
        // <body>, still inside this modal, or on a companion of it (a
        // toast's Undo the focus trap lets Tab reach — it goes when the
        // toast does). A menu item that opens an input (a day's Theme, a
        // walk's Rename) focuses it before this runs; pulling focus back to
        // the opener blurred the input, which saves and closes on blur.
        const active = document.activeElement;
        const focusIsFree = !active || active === document.body || !!modalNode?.contains(active)
          || !!active.closest?.('[data-modal-companion]');
        if (focusIsFree && previousActiveElement.current && typeof previousActiveElement.current.focus === 'function') {
          previousActiveElement.current.focus();
        }
      };
    }
  }, [isOpen]);

  // Event listeners for escape and focus trap (safe to re-attach on handler changes)
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.addEventListener('keydown', handleTabKey);
      return () => {
        document.removeEventListener('keydown', handleEscape);
        document.removeEventListener('keydown', handleTabKey);
      };
    }
  }, [isOpen, handleEscape, handleTabKey]);

  if (!isOpen) return null;

  // Size classes
  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl',
    '3xl': 'max-w-3xl',
    '4xl': 'max-w-4xl',
    full: 'max-w-full mx-4'
  };

  // Position classes
  const positionClasses = {
    center: 'items-center justify-center',
    top: 'items-start justify-center pt-16',
    bottom: 'items-end justify-center pb-4'
  };

  // For mobileFullScreen, a full-height (h-[100dvh]) sheet must top-anchor on mobile so
  // its header hugs the top edge. The desktop `bottom`/`top` offset padding (pb-4 / pt-16)
  // combined with items-end/items-start against the `fixed inset-0` container (which tracks
  // the larger layout viewport) otherwise shifts the sheet — clipping the header when the
  // URL bar is hidden, or gapping it when shown. `center` dialogs are left untouched.
  // Position layout is restored from md+, where the modal is an anchored dialog again.
  const positionClassesFullScreen = {
    center: 'items-center justify-center',
    top: 'items-start justify-center md:pt-16',
    bottom: 'items-start justify-center md:items-end md:pb-4'
  };
  const outerPositionClasses = mobileFullScreen
    ? positionClassesFullScreen[position]
    : positionClasses[position];

  // Backdrop classes
  const backdropClasses = {
    default: 'bg-black/50',
    blur: 'bg-black/40 backdrop-blur-sm',
    dark: 'bg-black/70',
    none: 'bg-transparent'
  };

  // Mobile full screen handling
  // flex flex-col + overflow-y-auto lets Body/Footer use flex-1 / flex-shrink-0
  // to pin the footer at the bottom, while still falling back to outer scroll
  // for modals that use raw content without Modal.Body.
  const mobileClasses = mobileFullScreen
    ? 'h-[100dvh] w-full md:h-auto md:w-full md:max-h-[90dvh] rounded-none md:rounded-xl flex flex-col overflow-y-auto'
    : 'max-h-[90dvh] rounded-xl flex flex-col overflow-y-auto';

  // When mobileFullScreen, remove outer padding on mobile so modal is edge-to-edge
  const outerPaddingClasses = mobileFullScreen ? 'p-0 md:p-4' : 'p-4';

  // A modal opened while another is already open uses the NESTED_* tokens so
  // it paints above its parent. Without this, both sit at MODAL and the
  // winner is whichever portal happened to mount last.
  const isNested = stackDepth > 0;
  const layer = isNested
    ? { backdrop: Z_INDEX.NESTED_MODAL_BACKDROP, container: Z_INDEX.NESTED_MODAL, content: Z_INDEX.NESTED_MODAL_CONTENT }
    : { backdrop: Z_INDEX.MODAL_BACKDROP, container: Z_INDEX.MODAL, content: Z_INDEX.MODAL_CONTENT };

  const modalContent = (
    <div
      className={`fixed inset-0 flex ${outerPositionClasses} ${outerPaddingClasses} overflow-y-auto ${className}`}
      style={{ zIndex: layer.container }}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-label={resolvedAriaLabel}
    >
      {/* Backdrop */}
      <div
        className={`fixed inset-0 ${backdropClasses[backdrop]} animate-modal-overlay`}
        style={{ zIndex: layer.backdrop }}
        aria-hidden="true"
      />

      {/* Modal Content */}
      <div
        ref={modalRef}
        // text-content-primary: anything inside that doesn't name its own
        // colour follows the theme — without it, unstyled labels and icons
        // (a form's option tiles) were black on the dark-mode surface.
        className={`relative w-full ${sizeClasses[size]} ${mobileClasses} bg-surface text-content-primary shadow-elevation-4 animate-modal-content overflow-hidden ${contentClassName}`}
        style={{ zIndex: layer.content }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        {showCloseButton && onClose && (
          <CloseButton
            onClick={onClose}
            className="absolute top-1.5 right-1.5 z-raised"
            ariaLabel={t('modal.closeButtonAriaLabel')}
          />
        )}

        {children}
      </div>
    </div>
  );

  // Use portal to render at document root
  return createPortal(modalContent, document.body);
}

/**
 * Modal Header component for consistent header styling
 */
Modal.Header = function ModalHeader({ children, className = '', onClose, showCloseButton = true }) {
  const { t } = useTranslation();
  return (
    <div className={`flex-shrink-0 px-4 py-3 border-b border-border flex items-center justify-between ${className}`}>
      {/* min-w-0: a long title truncates instead of widening the header past
          the panel, which pushed the close button off and let the whole sheet
          pan sideways (the When sheet on "Basilica and Expiatory Church of
          the Holy Family"). */}
      <div className="flex-1 min-w-0">
        {typeof children === 'string' ? (
          <h3 className="text-lg font-semibold text-content-primary">{children}</h3>
        ) : (
          children
        )}
      </div>
      {showCloseButton && onClose && (
        <CloseButton
          onClick={onClose}
          className="ml-2 -my-1.5 -mr-2"
          ariaLabel={t('modal.header.closeButtonAriaLabel')}
        />
      )}
    </div>
  );
};

/**
 * Modal Body component for consistent body styling
 */
Modal.Body = function ModalBody({ children, className = '', noPadding = false }) {
  return (
    <div className={`${noPadding ? '' : 'px-4 py-4'} flex-1 min-h-0 overflow-y-auto ${className}`}>
      {children}
    </div>
  );
};

/**
 * Modal Footer component for consistent footer styling
 */
Modal.Footer = function ModalFooter({ children, className = '' }) {
  return (
    <div className={`flex-shrink-0 px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] border-t border-border bg-background ${className}`}>
      {children}
    </div>
  );
};

export default Modal;
